import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getCsrfToken } from '@/utils/api';
import { Editor } from '@/ui/Editor';

type Category = { id: string; name: string; slug: string };
type PostBrief = { id: string; title: string; slug: string; excerpt?: string };

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [content, setContent] = useState('<p></p>');
  const [saving, setSaving] = useState(false);
  const [catName, setCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);
  const [posts, setPosts] = useState<PostBrief[]>([]);
  const csrf = getCsrfToken();

  useEffect(() => {
    document.title = 'Yönetim Paneli | ESDFL Blog';
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get('/api/admin/categories');
        if (!ignore) setCategories(res.categories ?? []);
      } catch (e) {
        navigate('/__admin');
      }
    })();
    (async () => {
      try {
        const res = await api.get('/api/admin/posts');
        if (!ignore) setPosts(res.posts ?? []);
      } catch {}
    })();
    return () => {
      ignore = true;
    };
  }, [navigate]);

  const onCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingCat(true);
    try {
      const res = await api.post('/api/admin/categories', { name: catName }, csrf);
      setCategories((c) => [res.category, ...c]);
      setCatName('');
    } finally {
      setCreatingCat(false);
    }
  };

  const onDeleteCategory = async (id: string) => {
    if (!confirm('Bu kategoriyi silmek istediğinize emin misiniz? İlgili yazılar da silinecek.')) return;
    await api.del(`/api/admin/categories/${id}`, undefined, csrf);
    setCategories((c) => c.filter((x) => x.id !== id));
    setPosts((p) => p.filter((x) => x));
  };

  const onDeletePost = async (id: string) => {
    if (!confirm('Bu yazıyı silmek istediğinize emin misiniz?')) return;
    await api.del(`/api/admin/posts/${id}`, undefined, csrf);
    setPosts((p) => p.filter((x) => x.id !== id));
  };

  const onPublish = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let coverImageUrl: string | undefined;
      if (coverFile) {
        const fd = new FormData();
        fd.append('file', coverFile);
        const upload = await api.upload('/api/admin/upload', fd, csrf);
        coverImageUrl = upload.url;
      }
      await api.post('/api/admin/posts', { title, categoryId, coverImageUrl, contentHtml: content }, csrf);
      setTitle('');
      setCategoryId('');
      setCoverFile(null);
      setContent('<p></p>');
      alert('Yazı yayınlandı');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Yönetim Paneli</h1>
        <div className="flex items-center gap-2">
          <form onSubmit={async (e) => { e.preventDefault(); await api.post('/api/admin/logout', {}, csrf); navigate('/__admin'); }}>
            <button className="btn-secondary" type="submit">Çıkış Yap</button>
          </form>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 card p-4">
          <h2 className="font-semibold mb-4">Yeni Yazı</h2>
          <form onSubmit={onPublish} className="space-y-4">
            <div>
              <label className="label">Başlık</label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Yazı başlığı" required />
            </div>
            <div>
              <label className="label">Kategori</label>
              <select className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                <option value="" disabled>Seçiniz</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Kapak Görseli</label>
              <input className="input" type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)} />
            </div>
            <div>
              <label className="label">İçerik</label>
              <Editor value={content} onChange={setContent} />
            </div>
            <button type="submit" className="btn" disabled={saving}>{saving ? 'Yayınlanıyor…' : 'Yayınla'}</button>
          </form>
        </section>

        <aside className="card p-4">
          <h2 className="font-semibold mb-4">Kategoriler</h2>
          <form onSubmit={onCreateCategory} className="flex items-center gap-2 mb-4">
            <input className="input" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Kategori adı" required />
            <button className="btn" type="submit" disabled={creatingCat}>Ekle</button>
          </form>
          <ul className="space-y-2">
            {categories.map((c) => (
              <li key={c.id} className="flex items-center justify-between">
                <span>{c.name}</span>
                <button className="btn-secondary" type="button" onClick={() => onDeleteCategory(c.id)}>Sil</button>
              </li>
            ))}
          </ul>
          <h2 className="font-semibold mt-6 mb-2">Yazılar</h2>
          <ul className="space-y-2 max-h-72 overflow-auto pr-1">
            {posts.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3">
                <span className="truncate" title={p.title}>{p.title}</span>
                <div className="flex items-center gap-2">
                  <button className="btn-secondary" type="button" onClick={() => onDeletePost(p.id)}>Sil</button>
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}


